// ==========================================================
// Multi-Language Localization Dictionaries (JA / EN)
// ==========================================================

export type Language = 'ja' | 'en';

export interface TranslationSchema {
  app: {
    title: string;
    subtitle: string;
    tagline: string;
    description: string;
    tip: string;
    totalScoreLabel: string;
    starsCollected: string;
    newHighScore: string;
    bestScore: string;
    noRecord: string;
    coinsLabel: string;
    shopBtn: string;
    allCategories: string;
    unlockWithCoins: string;
    locked: string;
    earnedCoins: string;
  };
  modes: {
    career: string;
    colorlab: string;
    free: string;
  };
  categories: {
    furniture: string;
    vehicles: string;
    music: string;
    gourmet: string;
    toys: string;
    crafts: string;
    scifi: string;
    animals: string;
    fashion: string;
    relics: string;
    landmarks: string;
  };
  shop: {
    title: string;
    subtitle: string;
    balanceLabel: string;
    watchAdTitle: string;
    watchAdDesc: string;
    watchAdBtn: string;
    vipTitle: string;
    vipDesc: string;
    vipBtn: string;
    packsTitle: string;
    packStarter: string;
    packMeister: string;
    closeBtn: string;
    freeNotice: string;
    freeNoticeTermsLink: string;
  };
  legal: {
    about: string;
    contact: string;
    privacy: string;
    terms: string;
    copyright: string;
    houseAdBadge: string;
    houseAdDisclosure: string;
  };
  difficulties: {
    '初級': string;
    '中級': string;
    '上級': string;
    '塗聖級': string;
  };
  hud: {
    backMenuTitle: string;
    accuracyLabel: string;
    accuracyTooltip: string;
    finishLabel: string;
    finishTooltip: string;
    soundTitle: string;
    finishBtn: string;
    finishBtnTitle: string;
    tutorialBtn: string;
    tutorialTitle: string;
    specTitle: string;
    targetColors: string;
  };
  tools: {
    brush: { name: string; title: string };
    roller: { name: string; title: string };
    spray: { name: string; title: string };
    tape: { name: string; title: string };
    peel: { name: string; title: string };
    peelAll: { name: string; title: string };
    sponge: { name: string; title: string };
    gloss: { name: string; title: string };
    hand: { name: string; title: string };
    sizeFine: string;
    sizeMedium: string;
    sizeBroad: string;
    sizeLabel: string;
    labBtn: string;
    labBtnTitle: string;
    undoTitle: string;
    clearTitle: string;
  };
  zoom: {
    zoomIn: string;
    zoomOut: string;
    reset: string;
    hand: string;
  };
  colorLab: {
    title: string;
    subtitle: string;
    targetLabel: string;
    currentLabel: string;
    matchLabel: string;
    mixingStatus: string;
    cyan: string;
    magenta: string;
    yellow: string;
    black: string;
    white: string;
    stirBtn: string;
    resetBtn: string;
    closeBtn: string;
    applyBtn: string;
    ratings: {
      perfect: string;
      great: string;
      good: string;
      mixing: string;
    };
  };
  result: {
    passHeadline: string;
    failHeadline: string;
    accuracyLabel: string;
    finishLabel: string;
    speedLabel: string;
    colorLabel: string;
    totalLabel: string;
    unitPts: string;
    saveImage: string;
    saveImageTitle: string;
    retry: string;
    nextStage: string;
    stamps: {
      meister: string;
      expert: string;
      artisan: string;
      apprentice: string;
    };
    stampSub: string;
    stampJp: string;
  };
  tutorial: {
    modalTitle: string;
    modalSub: string;
    tabs: {
      objective: string;
      tools: string;
      masking: string;
      controls: string;
    };
    sections: {
      objective: {
        badge: string;
        title: string;
        desc: string;
        steps: Array<{ title: string; desc: string }>;
      };
      tools: {
        badge: string;
        title: string;
        desc: string;
        tools: Array<{ icon: string; name: string; role: string; desc: string }>;
      };
      masking: {
        badge: string;
        title: string;
        desc: string;
        steps: Array<{ num: string; title: string; desc: string }>;
      };
      controls: {
        badge: string;
        title: string;
        desc: string;
        tips: Array<{ title: string; desc: string }>;
      };
    };
    closeBtn: string;
    startBtn: string;
  };
  blueprint: {
    zoomBtnTooltip: string;
    titleBadge: string;
    hintText: string;
    gridBtn: string;
    requiredColors: string;
    zoomIn: string;
    zoomOut: string;
    reset: string;
    closeBtn: string;
  };
}

export const TRANSLATIONS: Record<Language, TranslationSchema> = {
  ja: {
    app: {
      title: 'ペインター・マイスター',
      subtitle: '〜極・塗師道〜',
      tagline: 'PREMIUM CRAFTSMANSHIP SIMULATOR',
      description: '色を操り、筆を走らせ、至高の塗りを極めよ。全112ステージ＆11コレクション完全収録の職人ペイントゲーム。',
      tip: '💡 職人の心得: 輪郭は「刷毛」、広面は「ローラー」、立体感は「スプレー」。マスキングテープで綺麗に境界を分けよう！',
      totalScoreLabel: '総職人スコア',
      starsCollected: '獲得スター',
      newHighScore: '🏆 ハイスコア更新！',
      bestScore: '自己ベスト',
      noRecord: '未挑戦',
      coinsLabel: '所持コイン',
      shopBtn: '🏪 ショップ / VIP',
      allCategories: '🌟 全作品 (112)',
      unlockWithCoins: '🪙 {coins} で解放',
      locked: '🔒 鍵付き',
      earnedCoins: '獲得コイン'
    },
    modes: {
      career: '🔨 職人修行 (112 Stages)',
      colorlab: '⚗️ 調合マスター (Color Lab)',
      free: '🎨 フリー工房 (Free Studio)'
    },
    categories: {
      furniture: '🪑 家具',
      vehicles: '🏎️ 乗り物',
      music: '🎸 楽器',
      gourmet: '🍦 スイーツ',
      toys: '🤖 ホビー',
      crafts: '🐱 伝統工芸',
      scifi: '🚀 宇宙',
      animals: '🐾 動物',
      fashion: '👟 ストリート',
      relics: '👑 伝説秘宝',
      landmarks: '🏯 建造物・名所'
    },
    shop: {
      title: '職人の資材ショップ ＆ VIPパス',
      subtitle: 'コインを獲得して新しいステージをアンロック！VIPパスで全112ステージを即時解放！すべて無料でご利用いただけます。',
      balanceLabel: '現在の所持コイン',
      watchAdTitle: '🎬 広告を見てコインを獲得',
      watchAdDesc: '短い広告を最後まで視聴すると、工房コイン +500 を進呈します。視聴は任意です。',
      watchAdBtn: '広告を見て +500 🪙 獲得',
      vipTitle: '👑 VIP職人パス (Master Supporter)',
      vipDesc: '全112ステージ即時全解放 ＋ 広告完全非表示 ＋ 獲得コイン1.5倍ブースト！（無料・実際のお支払いは発生しません）',
      vipBtn: '👑 VIPパスを有効化する',
      packsTitle: '🪙 コインパック（無料）',
      packStarter: '見習いパック (+1,000 🪙)',
      packMeister: '塗聖の財宝パック (+5,000 🪙)',
      closeBtn: '閉じる',
      freeNotice: '💡 このショップの内容はすべて無料です。コインは換金性のないゲーム内ポイントで、実際の金銭のお支払いは一切発生しません。詳しくは',
      freeNoticeTermsLink: '利用規約'
    },
    legal: {
      about: '運営者情報 (About Us)',
      contact: 'お問い合わせ (Contact)',
      privacy: 'プライバシーポリシー',
      terms: '利用規約',
      copyright: '© {year} Paint Meister Studio. All rights reserved.',
      houseAdBadge: '📣 ゲーム内お知らせ (House Promo)',
      houseAdDisclosure: '※ これは本ゲーム自身のお知らせです。外部の広告ではありません。'
    },
    difficulties: {
      '初級': '初級',
      '中級': '中級',
      '上級': '上級',
      '塗聖級': '塗聖級'
    },
    hud: {
      backMenuTitle: 'メニューに戻る',
      accuracyLabel: '正確さ',
      accuracyTooltip: '塗りの正確さ (塗り残し・はみ出し判定)',
      finishLabel: '綺麗さ',
      finishTooltip: '塗りの綺麗さ (均一性・ムラのなさ)',
      soundTitle: 'サウンド切替',
      finishBtn: '完成・検品',
      finishBtnTitle: '塗装を完了して評価を受ける',
      tutorialBtn: '遊び方',
      tutorialTitle: 'ゲームの目的と操作説明を見る',
      specTitle: '見本指示書',
      targetColors: '指定カラー'
    },
    tools: {
      brush: { name: '刷毛', title: '刷毛 (Fine Brush) - エッジ塗り・高精度' },
      roller: { name: 'ローラー', title: 'ローラー (Wide Roller) - 広範囲均一塗り' },
      spray: { name: 'スプレー', title: 'スプレー (Airbrush) - グラデーション・ツヤ' },
      tape: { name: 'テープ', title: 'マスキングテープ (Masking Tape) - 境界保護' },
      peel: { name: '剥がす', title: 'タップして1本ずつ剥がす (Peel Single Tape)' },
      peelAll: { name: '全剥がし', title: 'すべてのテープを一括で剥がす (Peel All Tapes)' },
      sponge: { name: 'スポンジ', title: 'スポンジ (Eraser / Thinner) - 修正・薄め' },
      gloss: { name: 'ツヤ', title: '仕上げクリアコート (Top Coat)' },
      hand: { name: '移動', title: '画面移動 (Pan / Hand)' },
      sizeFine: '細',
      sizeMedium: '中',
      sizeBroad: '太',
      sizeLabel: '太さ',
      labBtn: '調合ラボ',
      labBtnTitle: '塗料調合ラボ (Color Mixing Lab)',
      undoTitle: '1手戻す',
      clearTitle: '塗り直し'
    },
    zoom: {
      zoomIn: '拡大 (Zoom In)',
      zoomOut: '縮小 (Zoom Out)',
      reset: '全体表示 (Reset 100%)',
      hand: 'パン移動 (Hand / Move)'
    },
    colorLab: {
      title: '職人の塗料調合ラボ',
      subtitle: 'CMYK＋白の原色ピグメントを配合し、見本色を完全再現せよ！',
      targetLabel: '見本色 (Target)',
      currentLabel: '調合色 (Current)',
      matchLabel: '一致度',
      mixingStatus: '調合中...',
      cyan: 'シアン (C)',
      magenta: 'マゼンタ (M)',
      yellow: 'イエロー (Y)',
      black: 'ブラック (K)',
      white: 'ホワイト (W)',
      stirBtn: '混ぜ合わせる (Stir) 🌀',
      resetBtn: 'リセット 🧼',
      closeBtn: '閉じる',
      applyBtn: 'この色をパレットに追加',
      ratings: {
        perfect: '神業の一致！ (98%+)',
        great: '極上の調合 (90%+)',
        good: '良好な調合 (80%+)',
        mixing: '調合中...'
      }
    },
    result: {
      passHeadline: '塗装作業 検品完了！',
      failHeadline: '再挑戦が必要です',
      accuracyLabel: '🎯 正確さ (塗り残し・はみ出し)',
      finishLabel: '✨ 綺麗さ (塗膜均一・ツヤ)',
      speedLabel: '⏱️ 速さ (タイムボーナス)',
      colorLabel: '⚗️ 調合精度 (色差一致度)',
      totalLabel: '総合職人スコア',
      unitPts: 'pts',
      saveImage: '作品を保存',
      saveImageTitle: '作品を高画質画像で保存',
      retry: '再挑戦',
      nextStage: '次のステージへ',
      stamps: {
        meister: '塗聖',
        expert: '免許皆伝',
        artisan: '一人前職人',
        apprentice: '見習い職人'
      },
      stampSub: 'PAINT MEISTER',
      stampJp: '極・塗師道'
    },
    tutorial: {
      modalTitle: '職人修業の手引き (How to Play)',
      modalSub: 'ゲームの目的・基本操作・プロ職人の技を完全マスターしよう！',
      tabs: {
        objective: '🎯 ゲームの目的',
        tools: '🖌️ 塗装ツール',
        masking: '📏 マスキングの極意',
        controls: '⚗️ 調合＆操作'
      },
      sections: {
        objective: {
          badge: 'GOAL & RULES',
          title: '見本通りの美しい塗装で最高評価を目指せ！',
          desc: 'プレイヤーの使命は、依頼された家具や工芸品を見本（Target Spec）通りに美しく塗り分けることです。',
          steps: [
            {
              title: '1. 見本指示書を確認',
              desc: '右上の「見本指示書」で目標の配色とデザインをチェックします。'
            },
            {
              title: '2. 正確＆迅速に塗装',
              desc: 'はみ出しを抑え、塗り残しなく均一に仕上げます。早く完成させるとタイムボーナス！'
            },
            {
              title: '3. 完成・検品で評価獲得',
              desc: '「完成・検品」ボタンを押すと職人検品が実施され、★1〜3の星と職人称号（徒弟〜塗聖）が授与されます。'
            }
          ]
        },
        tools: {
          badge: 'TOOL MASTERY',
          title: '状況に応じて4大ツールを使い分けよう',
          desc: '各ツールには独自の特性があります。適材適所で使い分けるのが一流職人への近道です。',
          tools: [
            {
              icon: '🖌️',
              name: '刷毛 (Fine Brush)',
              role: 'エッジ塗り・細部',
              desc: '筆先が硬く繊細な線が引けます。境界付近の丁寧な塗り分けに最適です。'
            },
            {
              icon: '🧹',
              name: 'ローラー (Wide Roller)',
              role: '広範囲・均一塗り',
              desc: '幅広で一度に広い面をムラなく素早く塗ることができます。'
            },
            {
              icon: '💨',
              name: 'スプレー (Airbrush)',
              role: 'グラデーション・ツヤ',
              desc: '柔らかいボケ足と滑らかなグラデーション塗装を表現できます。'
            },
            {
              icon: '🧽',
              name: 'スポンジ (Eraser)',
              role: 'はみ出し修正・薄め',
              desc: '塗りすぎてはみ出した塗料を優しく拭き取って修正します。'
            }
          ]
        },
        masking: {
          badge: 'PRO TECHNIQUE',
          title: 'マスキングテープで完璧なストレートライン！',
          desc: '曲線や直線のはみ出しを防ぎ、シャープな塗膜を作るための必須テクニックです。',
          steps: [
            {
              num: 'STEP 1',
              title: 'テープを貼る (📏 テープ)',
              desc: '境界線に沿ってドラッグするとマスキングテープが設置され、その下の領域が保護されます。'
            },
            {
              num: 'STEP 2',
              title: '上から大胆に塗装',
              desc: 'テープの上からはみ出しても大丈夫！気にせず豪快にローラーやスプレーで塗りましょう。'
            },
            {
              num: 'STEP 3',
              title: 'テープを剥がす (✂️ 剥がす / 🧹✂️ 全剥がし)',
              desc: 'テープを剥がすと、一切のにじみがない完璧な境界ラインが現れます！'
            }
          ]
        },
        controls: {
          badge: 'MIXING & NAVIGATION',
          title: '塗料の調合とキャンバスの快適操作',
          desc: '本格的な塗料調合と、ズーム・パンによる精密作業をマスターしましょう。',
          tips: [
            {
              title: '⚗️ 塗料調合ラボ (Color Mixing)',
              desc: 'シアン・マゼンタ・イエロー・ブラック・ホワイトの滴数を調整して混ぜ合わせ、目標の色を調合します。'
            },
            {
              title: '🔍 ズーム & パン移動 (Zoom & Pan)',
              desc: 'マウスホイールや2本指ピンチでズーム。✋ハンドツールまたは2本指ドラッグでキャンバスを自由に移動できます。'
            },
            {
              title: '↩️ アンドゥ & 塗り直し',
              desc: '失敗しても「1手戻す」や「塗り直し」でいつでも安心してリトライできます。'
            }
          ]
        }
      },
      closeBtn: '閉じる',
      startBtn: '修行を開始する！ 🎨'
    },
    blueprint: {
      zoomBtnTooltip: '見本指示書を拡大表示 (Space / Z)',
      titleBadge: '見本指示書・高精細図面',
      hintText: 'ドラッグで移動 / ホイールまたはピンチで拡大縮小 (100%〜300%)',
      gridBtn: '📐 方眼グリッド',
      requiredColors: '使用指定色:',
      zoomIn: '拡大',
      zoomOut: '縮小',
      reset: '100% 戻す',
      closeBtn: '閉じる (ESC / Space)'
    }
  },

  en: {
    app: {
      title: 'Paint Meister',
      subtitle: '〜Way of the Artisan〜',
      tagline: 'PREMIUM CRAFTSMANSHIP SIMULATOR',
      description: 'Master the art of painting, master colors, and achieve perfection. 112 stages across 11 collections, for mobile and desktop.',
      tip: '💡 Master Tip: Use Brush for edges, Roller for broad areas, and Spray for gradients. Use Masking Tape for crisp straight lines!',
      totalScoreLabel: 'Total Craft Score',
      starsCollected: 'Stars Collected',
      newHighScore: '🏆 New High Score!',
      bestScore: 'Best Score',
      noRecord: 'Unplayed',
      coinsLabel: 'Atelier Coins',
      shopBtn: '🏪 Shop / VIP',
      allCategories: '🌟 All (112)',
      unlockWithCoins: '🪙 {coins} to Unlock',
      locked: '🔒 Locked',
      earnedCoins: 'Coins Earned'
    },
    modes: {
      career: '🔨 Career (112 Stages)',
      colorlab: '⚗️ Color Lab Master',
      free: '🎨 Free Studio'
    },
    categories: {
      furniture: '🪑 Furniture',
      vehicles: '🏎️ Vehicles',
      music: '🎸 Music',
      gourmet: '🍦 Gourmet',
      toys: '🤖 Toys',
      crafts: '🐱 Crafts',
      scifi: '🚀 Sci-Fi',
      animals: '🐾 Animals',
      fashion: '👟 Street',
      relics: '👑 Relics',
      landmarks: '🏯 Landmarks'
    },
    shop: {
      title: 'Artisan Materials Shop & VIP Pass',
      subtitle: 'Earn coins to unlock new stages, or activate the VIP Pass to open all 112 stages at once — everything here is free.',
      balanceLabel: 'Current Balance',
      watchAdTitle: '🎬 Watch an Ad for Coins',
      watchAdDesc: 'Watch a short ad through to the end and receive +500 Atelier Coins. Watching is optional.',
      watchAdBtn: 'Watch Ad for +500 🪙',
      vipTitle: '👑 VIP Meister Pass (Supporter)',
      vipDesc: 'Instantly unlock all 112 stages + 100% ad-free + 1.5x coin boost (free — no real payment is ever taken).',
      vipBtn: '👑 Activate VIP Master Pass',
      packsTitle: '🪙 Coin Packs (free)',
      packStarter: 'Apprentice Pack (+1,000 🪙)',
      packMeister: 'Grand Meister Vault (+5,000 🪙)',
      closeBtn: 'Close',
      freeNotice: '💡 Everything in this shop is free. Coins are in-game points with no cash value, and no real payment is ever taken. See our ',
      freeNoticeTermsLink: 'Terms of Use'
    },
    legal: {
      about: 'About Us',
      contact: 'Contact Us',
      privacy: 'Privacy Policy',
      terms: 'Terms of Use',
      copyright: '© {year} Paint Meister Studio. All rights reserved.',
      houseAdBadge: '📣 In-game notice (House Promo)',
      houseAdDisclosure: 'This is a notice about the game itself, not a third-party advertisement.'
    },
    difficulties: {
      '初級': 'Beginner',
      '中級': 'Intermediate',
      '上級': 'Advanced',
      '塗聖級': 'Grand Meister'
    },
    hud: {
      backMenuTitle: 'Return to Menu',
      accuracyLabel: 'Accuracy',
      accuracyTooltip: 'Painting accuracy (Coverage vs Bleed)',
      finishLabel: 'Finish',
      finishTooltip: 'Finish quality (Smoothness and uniformity)',
      soundTitle: 'Toggle Sound',
      finishBtn: 'Inspect & Finish',
      finishBtnTitle: 'Submit work for master inspection',
      tutorialBtn: 'How to Play',
      tutorialTitle: 'View game objectives and controls guide',
      specTitle: 'Target Spec',
      targetColors: 'Target Colors'
    },
    tools: {
      brush: { name: 'Brush', title: 'Fine Brush - Precision & Edge Painting' },
      roller: { name: 'Roller', title: 'Wide Roller - Fast & Uniform Coverage' },
      spray: { name: 'Spray', title: 'Airbrush - Smooth Gradients & Gloss' },
      tape: { name: 'Tape', title: 'Masking Tape - Boundary Protection' },
      peel: { name: 'Peel', title: 'Tap to peel individual tape' },
      peelAll: { name: 'Peel All', title: 'Peel all masking tapes at once' },
      sponge: { name: 'Sponge', title: 'Sponge / Thinner - Erase & Clean' },
      gloss: { name: 'Gloss', title: 'Top Coat Clear Finish' },
      hand: { name: 'Pan', title: 'Pan / Hand Move Tool' },
      sizeFine: 'S',
      sizeMedium: 'M',
      sizeBroad: 'L',
      sizeLabel: 'Size',
      labBtn: 'Color Lab',
      labBtnTitle: 'Color Mixing Lab',
      undoTitle: 'Undo Last Action',
      clearTitle: 'Clear Paint'
    },
    zoom: {
      zoomIn: 'Zoom In',
      zoomOut: 'Zoom Out',
      reset: 'Reset (100%)',
      hand: 'Pan / Hand Move'
    },
    colorLab: {
      title: 'Artisan Color Mixing Lab',
      subtitle: 'Blend CMYK + White pigments to perfectly reproduce the target color!',
      targetLabel: 'Target Color',
      currentLabel: 'Mixed Color',
      matchLabel: 'Match Score',
      mixingStatus: 'Mixing...',
      cyan: 'Cyan (C)',
      magenta: 'Magenta (M)',
      yellow: 'Yellow (Y)',
      black: 'Black (K)',
      white: 'White (W)',
      stirBtn: 'Stir & Mix 🌀',
      resetBtn: 'Reset 🧼',
      closeBtn: 'Close',
      applyBtn: 'Add to Palette',
      ratings: {
        perfect: 'Divine Match! (98%+)',
        great: 'Exquisite Blend (90%+)',
        good: 'Good Blend (80%+)',
        mixing: 'Mixing...'
      }
    },
    result: {
      passHeadline: 'Inspection Complete!',
      failHeadline: 'Retry Needed',
      accuracyLabel: '🎯 Accuracy (Coverage & Bleed)',
      finishLabel: '✨ Finish Quality (Smoothness)',
      speedLabel: '⏱️ Speed (Time Bonus)',
      colorLabel: '⚗️ Color Accuracy (Color Match)',
      totalLabel: 'Total Artisan Score',
      unitPts: 'pts',
      saveImage: 'Save Artwork',
      saveImageTitle: 'Save high-resolution PNG image',
      retry: 'Retry Stage',
      nextStage: 'Next Stage',
      stamps: {
        meister: 'Grand Meister',
        expert: 'Master Artisan',
        artisan: 'Skilled Artisan',
        apprentice: 'Apprentice'
      },
      stampSub: 'PAINT MEISTER',
      stampJp: 'Way of the Artisan'
    },
    tutorial: {
      modalTitle: 'Artisan Handbook (How to Play)',
      modalSub: 'Master the game objectives, core tools, and professional painting secrets!',
      tabs: {
        objective: '🎯 Objectives',
        tools: '🖌️ Paint Tools',
        masking: '📏 Masking Art',
        controls: '⚗️ Lab & Controls'
      },
      sections: {
        objective: {
          badge: 'GOAL & RULES',
          title: 'Paint the Target to Perfection & Achieve Meister Rank!',
          desc: 'Your mission is to paint crafted furniture, toys, and models matching the Target Spec provided.',
          steps: [
            {
              title: '1. Check the Target Spec',
              desc: 'Review the sample card at the top right for required color placements and patterns.'
            },
            {
              title: '2. Paint Accurately & Swiftly',
              desc: 'Minimize bleeding, cover completely, and work quickly to earn high time bonus points!'
            },
            {
              title: '3. Complete & Inspection',
              desc: 'Tap "Inspect & Finish" to undergo master inspection, earning 1-3 Stars and Artisan Titles!'
            }
          ]
        },
        tools: {
          badge: 'TOOL MASTERY',
          title: 'Master the 4 Essential Artisan Tools',
          desc: 'Each tool has unique physics and capabilities. Choosing the right tool is the key to mastery.',
          tools: [
            {
              icon: '🖌️',
              name: 'Fine Brush',
              role: 'Edges & Details',
              desc: 'Crisp, controlled strokes. Ideal for fine outlines, corners, and precision painting.'
            },
            {
              icon: '🧹',
              name: 'Wide Roller',
              role: 'Broad & Fast Coverage',
              desc: 'Broad, consistent application. Quickly fills large surfaces with uniform paint.'
            },
            {
              icon: '💨',
              name: 'Airbrush Spray',
              role: 'Gradients & Gloss',
              desc: 'Soft feathered edges and gentle spray dispersion for silky smooth color transitions.'
            },
            {
              icon: '🧽',
              name: 'Sponge Thinner',
              role: 'Erase & Cleanup',
              desc: 'Gently cleans off excess or spilled paint without damaging the base object.'
            }
          ]
        },
        masking: {
          badge: 'PRO TECHNIQUE',
          title: 'Crisp Straight Lines with Masking Tape!',
          desc: 'The secret weapon of professional painters: protect boundaries for razor-sharp transitions.',
          steps: [
            {
              num: 'STEP 1',
              title: 'Place Tape (📏 Tape)',
              desc: 'Drag across the object to place masking tape. The surface underneath is completely shielded.'
            },
            {
              num: 'STEP 2',
              title: 'Paint Boldly Over Tape',
              desc: 'Paint freely across the tape! Do not worry about overlapping boundaries.'
            },
            {
              num: 'STEP 3',
              title: 'Peel Tapes (✂️ Peel / 🧹✂️ Peel All)',
              desc: 'Peel the tape away to reveal a breathtaking, razor-sharp clean boundary!'
            }
          ]
        },
        controls: {
          badge: 'MIXING & NAVIGATION',
          title: 'Color Mixing & Effortless Viewport Controls',
          desc: 'Create custom hues and effortlessly navigate intricate details.',
          tips: [
            {
              title: '⚗️ Color Mixing Lab',
              desc: 'Adjust dropper counts of Cyan, Magenta, Yellow, Black, and White to match target colors.'
            },
            {
              title: '🔍 Zoom & Pan Navigation',
              desc: 'Use mouse wheel or 2-finger pinch to zoom. Use the ✋ Hand Tool or 2-finger drag to pan.'
            },
            {
              title: '↩️ Undo & Clean Canvas',
              desc: 'Made a mistake? Use Undo or Clear Canvas to freely restart anytime without penalty.'
            }
          ]
        }
      },
      closeBtn: 'Close',
      startBtn: 'Start Painting! 🎨'
    },
    blueprint: {
      zoomBtnTooltip: 'Inspect Blueprint in High-Res (Space / Z)',
      titleBadge: 'Stage Blueprint & Precision Inspection',
      hintText: 'Drag to pan / Wheel or pinch to zoom (100% - 300%)',
      gridBtn: '📐 Grid Overlay',
      requiredColors: 'Required Colors:',
      zoomIn: 'Zoom In',
      zoomOut: 'Zoom Out',
      reset: 'Reset (100%)',
      closeBtn: 'Close (ESC / Space)'
    }
  }
};
